<template>
  <div v-if="isVisible">
    <div :class="$style.overlay" @click="closeModal"></div>
    <div :class="$style.modal">
      <div :class="$style.header">
        <h3 :class="$style.title">권한관리</h3>
        <button :class="$style.closeButton" @click="closeModal" aria-label="닫기">×</button>
      </div>

      <div :class="$style.content">
        <p :class="$style.description">
          권한을 부여받은 사용자는 이 라이브의 수정, 삭제, 예매관리 권한을 갖게 됩니다.
        </p>

        <!-- 현재 멤버 목록 -->
        <div :class="$style.section">
          <h4 :class="$style.sectionTitle">관리 권한 보유자</h4>
          <div v-if="membersWithInfo.length === 0" :class="$style.emptyState">
            권한이 부여된 사용자가 없습니다.
          </div>
          <div v-else :class="$style.memberList">
            <div v-for="(memberData, index) in membersWithInfo" :key="index" :class="$style.memberItem">
              <div :class="$style.memberInfo">
                <img v-if="memberData.profileImage" :src="memberData.profileImage" :alt="memberData.nickname"
                  :class="$style.profileImage" />
                <div v-else :class="$style.profilePlaceholder">
                  {{ memberData.email ? memberData.email[0].toUpperCase() : '?' }}
                </div>
                <div :class="$style.memberDetails">
                  <span :class="$style.memberNickname">{{ memberData.nickname || '닉네임 없음' }}</span>
                  <span :class="$style.memberEmail">{{ memberData.email }}</span>
                </div>
              </div>
              <button :class="$style.removeButton" @click="removeMember(index)" :disabled="loading">
                삭제
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 멤버 추가 (content 밖으로 분리) -->
      <div :class="$style.addMemberSection">
        <h4 :class="$style.sectionTitle">권한 부여</h4>
        <div :class="$style.addMemberForm">
          <AutoComplete ref="userAutocomplete" v-model="searchQuery" :suggestions="filteredUsers"
            @complete="searchUsers" @item-select="selectUser" placeholder="닉네임 또는 이메일로 검색" :disabled="loading"
            optionLabel="label" :forceSelection="false" panelClass="user-autocomplete-panel"
            class="user-search-autocomplete" appendTo="self">
            <template #option="{ option }">
              <div class="user-option">
                <img v-if="option.profileImage" :src="option.profileImage" :alt="option.nickname"
                  class="option-profile-image" />
                <div v-else class="option-profile-placeholder">
                  {{ option.email ? option.email[0].toUpperCase() : '?' }}
                </div>
                <div class="option-details">
                  <span class="option-nickname">{{ option.nickname || '닉네임 없음' }}</span>
                  <span class="option-email">{{ option.email }}</span>
                </div>
              </div>
            </template>
          </AutoComplete>
          <button :class="$style.addButton" @click="addMemberBySearch" :disabled="loading || !selectedUser">
            추가
          </button>
        </div>
      </div>

      <div :class="$style.footer">
        <button :class="$style.cancelButton" @click="closeModal" :disabled="loading">닫기</button>
      </div>
    </div>
  </div>
</template>

<script>
import { updateLive } from "@/utils/write/live";
import { collection, query, where, getDocs } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";
import { createToast } from "mosha-vue-toastify";
import AutoComplete from "primevue/autocomplete";
import "mosha-vue-toastify/dist/style.css";

export default {
  name: "MemberManageModal",
  components: {
    AutoComplete,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    liveId: {
      type: String,
      required: true,
    },
    initialMembers: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "updated"],
  data() {
    return {
      members: [],
      membersWithInfo: [],
      searchQuery: "",
      filteredUsers: [],
      selectedUser: null,
      loading: false,
    };
  },
  watch: {
    isVisible: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.members = [...this.initialMembers];
          this.searchQuery = "";
          this.selectedUser = null;
          this.filteredUsers = [];
          await this.loadMembersInfo();
        }
      },
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    async loadMembersInfo() {
      if (this.members.length === 0) {
        this.membersWithInfo = [];
        return;
      }

      try {
        const membersInfo = [];

        for (const email of this.members) {
          try {
            const usersRef = collection(firestore, "users");
            const q = query(usersRef, where("email", "==", email));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
              const userData = querySnapshot.docs[0].data();
              let profileImageUrl = null;

              // 프로필 이미지가 있으면 storage에서 다운로드 URL 생성
              if (userData.img) {
                try {
                  const imageRef = storageRef(storage, `profile/${userData.img}`);
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch (error) {
                  console.warn(`프로필 이미지 URL 생성 실패 (${email}):`, error);
                  profileImageUrl = null;
                }
              }

              membersInfo.push({
                email: email,
                nickname: userData.nickname || null,
                profileImage: profileImageUrl,
              });
            } else {
              // 사용자 정보를 찾을 수 없는 경우
              membersInfo.push({
                email: email,
                nickname: null,
                profileImage: null,
              });
            }
          } catch (error) {
            console.error(`사용자 정보 조회 오류 (${email}):`, error);
            membersInfo.push({
              email: email,
              nickname: null,
              profileImage: null,
            });
          }
        }

        this.membersWithInfo = membersInfo;
      } catch (error) {
        console.error("멤버 정보 로딩 오류:", error);
        this.membersWithInfo = this.members.map(email => ({
          email,
          nickname: null,
          profileImage: null,
        }));
      }
    },
    async searchUsers(event) {
      const searchTerm = event.query.trim().toLowerCase();

      if (!searchTerm) {
        this.filteredUsers = [];
        return;
      }

      try {
        const usersRef = collection(firestore, "users");
        const allUsersSnapshot = await getDocs(usersRef);

        const results = [];

        for (const doc of allUsersSnapshot.docs) {
          const userData = doc.data();
          const email = userData.email?.toLowerCase() || "";
          const nickname = userData.nickname?.toLowerCase() || "";

          // 검색어가 이메일이나 닉네임에 포함되어 있는지 확인
          if (email.includes(searchTerm) || nickname.includes(searchTerm)) {
            let profileImageUrl = null;

            // 프로필 이미지 로드
            if (userData.img) {
              try {
                const imageRef = storageRef(storage, `profile/${userData.img}`);
                profileImageUrl = await getDownloadURL(imageRef);
              } catch (error) {
                console.warn(`프로필 이미지 로드 실패:`, error);
              }
            }

            results.push({
              email: userData.email,
              nickname: userData.nickname || null,
              profileImage: profileImageUrl,
              label: `${userData.nickname || '닉네임 없음'} (${userData.email})`,
            });
          }
        }

        this.filteredUsers = results.slice(0, 10); // 최대 10개까지만 표시
      } catch (error) {
        console.error("사용자 검색 오류:", error);
        this.filteredUsers = [];
      }
    },
    selectUser(event) {
      this.selectedUser = event.value;
      this.searchQuery = event.value.label;
    },
    async addMemberBySearch() {
      if (!this.selectedUser) {
        this.toast("사용자를 선택해주세요.", "warning");
        return;
      }

      const email = this.selectedUser.email;
      // 중복 체크
      if (this.members.includes(email)) {
        this.toast("이미 권한이 부여된 사용자입니다.", "warning");
        return;
      }

      try {
        this.loading = true;
        const updatedMembers = [...this.members, email];

        await updateLive(
          this.liveId,
          { organizer: updatedMembers },
          null, // posterFile
          null, // bannerFile
          null, // detailImageFile
          null  // detailImage2File
        );

        this.members = updatedMembers;
        this.searchQuery = "";
        this.selectedUser = null;
        this.filteredUsers = [];
        await this.loadMembersInfo();
        this.toast("권한이 부여되었습니다.", "success");
        this.$emit("updated", updatedMembers);
      } catch (error) {
        console.error("권한 부여 오류:", error);
        this.toast("권한 부여 중 오류가 발생했습니다.", "danger");
      } finally {
        this.loading = false;
      }
    },
    async removeMember(index) {
      const confirmed = await this.$confirm("이 사용자의 권한을 삭제하시겠습니까?")
      if (!confirmed) {
        return;
      }

      try {
        this.loading = true;
        const updatedMembers = this.members.filter((_, i) => i !== index);

        await updateLive(
          this.liveId,
          { organizer: updatedMembers.length > 0 ? updatedMembers : null },
          null, // posterFile
          null, // bannerFile
          null, // detailImageFile
          null  // detailImage2File
        );

        this.members = updatedMembers;
        await this.loadMembersInfo();
        this.toast("권한이 삭제되었습니다.", "success");
        this.$emit("updated", updatedMembers);
      } catch (error) {
        console.error("권한 삭제 오류:", error);
        this.toast("권한 삭제 중 오류가 발생했습니다.", "danger");
      } finally {
        this.loading = false;
      }
    },
    toast(message, type = "default") {
      createToast(message, { type, position: "top-center", showIcon: true });
    },
  },
};
</script>

<style module>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: visible;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: #333;
}

.closeButton {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.closeButton:hover {
  background: #f5f5f5;
  color: #666;
}

.content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  padding-bottom: 16px;
}

.description {
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
  line-height: 1.5;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
}

.section {
  margin-bottom: 24px;
}

.section:last-child {
  margin-bottom: 0;
}

.addMemberSection {
  padding: 20px 24px;
  position: relative;
  overflow: visible;
  z-index: 10;
}

.sectionTitle {
  font-size: 15px;
  font-weight: bold;
  margin: 0 0 12px 0;
  color: #333;
}

.emptyState {
  text-align: center;
  padding: 32px 16px;
  color: #999;
  font-size: 14px;
}

.memberList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.memberItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: background 0.2s;
}

.memberItem:hover {
  background: #f0f1f3;
}

.memberInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.profileImage {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.profilePlaceholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
}

.memberDetails {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.memberNickname {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.memberEmail {
  font-size: 13px;
  color: #666;
  word-break: break-all;
}

.removeButton {
  padding: 6px 12px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-left: 12px;
}

.removeButton:hover:not(:disabled) {
  background: #d32f2f;
}

.removeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.addMemberForm {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  position: relative;
}

.addMemberForm :global(.user-search-autocomplete) {
  flex: 1;
  width: 100%;
  position: relative;
}

.addMemberForm :global(.user-search-autocomplete .p-autocomplete-input) {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.addMemberForm :global(.user-search-autocomplete .p-autocomplete-input:focus) {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.addMemberForm :global(.user-search-autocomplete .p-autocomplete-input:disabled) {
  background: #f5f5f5;
  cursor: not-allowed;
}

.addButton {
  padding: 10px 20px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  font-weight: 500;
}

.addButton:hover:not(:disabled) {
  background: #1976d2;
}

.addButton:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.addMemberSection .sectionTitle {
  font-size: 15px;
  font-weight: bold;
  margin: 0 0 12px 0;
  color: #333;
}

.footer {
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.cancelButton {
  padding: 10px 24px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.cancelButton:hover:not(:disabled) {
  background: #e0e0e0;
}

.cancelButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* AutoComplete 드롭다운 패널 스타일 */
:global(.user-autocomplete-panel) {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  width: 100% !important;
  max-width: none !important;
  z-index: 1100 !important;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
  margin-top: 4px !important;
}

:global(.user-autocomplete-panel .p-autocomplete-items) {
  padding: 4px 0;
}

:global(.user-autocomplete-panel .p-autocomplete-item) {
  padding: 0 !important;
  cursor: pointer;
  transition: background 0.2s;
}

:global(.user-autocomplete-panel .p-autocomplete-item:hover) {
  background: #f5f5f5;
}

:global(.user-autocomplete-panel .p-autocomplete-item.p-highlight) {
  background: #e3f2fd;
}

/* 사용자 옵션 스타일 */
:global(.user-option) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}

:global(.option-profile-image) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

:global(.option-profile-placeholder) {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  flex-shrink: 0;
}

:global(.option-details) {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

:global(.option-nickname) {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

:global(.option-email) {
  font-size: 12px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .modal {
    width: 95%;
    max-height: 90vh;
  }

  .header {
    padding: 16px 20px;
  }

  .content {
    padding: 20px;
  }

  .title {
    font-size: 16px;
  }

  .addMemberForm {
    flex-direction: column;
  }

  .addButton {
    width: 100%;
  }

  :global(.user-autocomplete-panel) {
    max-width: 90vw !important;
  }
}
</style>
